<template>
  <div class="autocomplete-container" @focus="focused = true">
    <input type="text" :placeholder="placeholder" :class="className" v-model="inputVal" @focus="focused = true"
           @blur="blur" @keydown="keyDown">
    <div :id="'autocomplete-results-' + id" v-show="showResults" class="autocomplete" @mouseleave="hoverIndex = null">
      <ul>
        <li
          v-for="(r, i) in results"
          :key="'autocomplete-' + i"
          :class="{ selected: hoverIndex === i }"
          @mouseenter="hoverIndex = i"
          @click="selectItem(i)">
          <span>{{ r }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
  export default {
    name: 'AutoComplete',
    data: () => ({
      results: [],
      inputVal: '',
      focused: false,
      hoverIndex: null
    }),
    props: {
      id: {
        type: String,
        default: 'autocomplete-input'
      },
      // If type is list, this determines whether or not we want to remove added items from results
      // TODO: Implement
      excludeSelected: {
        type: Boolean,
        default: false
      },
      type: {
        type: String,
        default: 'single'
      },
      // Simply an array of strings
      dictionary: {
        type: Array,
        default: null
      },
      className: {
        type: String,
        default: ''
      },
      placeholder: {
        type: String,
        default: "Start typing..."
      },
      value: {
        type: String
      }
    },
    watch: {
      inputVal() {
        this.search(document.getElementById(this.id), false)
        if (this.results.length) {
          this.hoverIndex = 0
          document.getElementById('autocomplete-results-' + this.id).scroll(0, 0)
        } else {
          this.hoverIndex = null
        }
        this.$emit('input', this.inputVal)
        this.$emit('change', this.inputVal)
      },
      value: {
        immediate: true,
        handler(val) {
          this.inputVal = val
        }
      }
    },
    computed: {
      showResults() {
        let inputVal = this.inputVal
        if (this.type === 'list') {
          let split = inputVal.split(',')
          if (split.length) {
            inputVal = split[split.length - 1]
          } else {
            inputVal = ""
          }
        }
        return this.results.length && this.focused
      }
    },
    methods: {
      selectItem(i) {
        let item = this.results[i]
        if (this.type === 'single') {
          this.inputVal = item
        } else {
          // Discard whatever is written after the last comma, and add selected results to the inputVal and a comma
          // When we unfocus the input (i.e. blur()), remove the comma at the end
          let split = this.inputVal.split(',')
          split.pop()
          split.push(item)
          this.inputVal = split.join(",") + ","
        }

        this.results = []
      },
      blur() {
        this.focused = false
        if (this.type === 'list' && this.inputVal[this.inputVal.length - 1] === ',') {
          this.inputVal = this.inputVal.substr(0, this.inputVal.length - 1)
        }
      },
      keyDown(e) {
        let cur
        switch (e.keyCode) {
          case 27: // ESC
            this.results = []
            break
          case 188: // Comma
            e.preventDefault()
          // fall through
          case 13: // Enter
            if (this.hoverIndex != null) {
              this.selectItem(this.hoverIndex)
            }
            break
          case 40: // KeyDown
            if (this.hoverIndex == null) {
              this.hoverIndex = 0
            } else if (this.hoverIndex === this.results.length - 1) {
              this.hoverIndex = 0
            } else {
              this.hoverIndex++
            }
            cur = document.getElementById('autocomplete-results-' + this.id).childNodes[0].childNodes[this.hoverIndex]
            if (cur) {
              document.getElementById('autocomplete-results-' + this.id).scroll(0, cur.offsetTop)
            }
            break
          case 38: // KeyUp
            if (this.hoverIndex === 0) {
              this.hoverIndex = this.results.length - 1
            } else {
              this.hoverIndex--
            }
            cur = document.getElementById('autocomplete-results-' + this.id).childNodes[0].childNodes[this.hoverIndex]
            if (cur) {
              document.getElementById('autocomplete-results-' + this.id).scroll(0, cur.offsetTop)
            }
            break
          case 32: // Space
            if (e.ctrlKey) {
              // If input is empty and results are shown, close them
              if (!this.inputVal.length && this.results.length) {
                this.results = []
              } else {
                this.search(e.target, true)
                if (this.results) {
                  this.hoverIndex = 0
                  document.getElementById('autocomplete-results-' + this.id).scroll(0, 0)
                }
              }
            }
            break
        }
      },
      search(el, acceptEmpty) {
        let inputVal = this.inputVal

        // If list, explode inputVal by comma. Use last item in resulting list as search input
        if (this.type === 'list') {
          let split = inputVal.split(',')
          inputVal = split[split.length - 1]
        }

        if (!acceptEmpty && !inputVal.trim().length) {
          this.results = []
          return
        }

        let caseSensitive = false

        for (let i = 0; i < inputVal.length; i++) {
          let char = inputVal.charAt(i)
          // We want a case insensitive search, unless one of the input characters are capitalized
          if (char.toUpperCase() === char) {
            caseSensitive = true
          }
        }

        // Search through dictionary with fuzzy search
        let reg = ""
        for (let i = 0; i < inputVal.length; i++) {
          let char = inputVal.charAt(i)

          if (!caseSensitive) {
            char = char.toLowerCase()
          }
          reg += "(.*)" + char
        }
        reg += "(.*)"
        reg = new RegExp(reg)


        // Temporary results list
        let matched = []

        for (let i = 0; i < this.dictionary.length; i++) {
          let val = this.dictionary[i]

          let searchVal = val
          if (!caseSensitive) {
            searchVal = val.toLowerCase()
          }

          let search = searchVal.search(reg)

          if (search !== -1) {
            matched.push(val)
          }
        }

        this.results = matched

        // TODO: Allow for caller of component to filter temporary results before displaying
      }
    }
  }
</script>

<style lang="sass">
  .autocomplete-container .autocomplete
    z-index: 200
</style>
